import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Calendar, CalendarCheck, Clock, MapPin, User, Phone, Mail, Trash2, AlertCircle, Users, Filter, Archive, DollarSign, Edit, Search, ChevronDown } from 'lucide-react';
import PaymentModal from '../components/PaymentModal';
import EditBookingModal from '../components/EditBookingModal';
import { useBookings, useBookingActions, usePayments, useSlotConfiguration } from '../hooks/booking/useBookingContextSlices';
import { parseISO, format } from 'date-fns';
import DashboardLayout from '../components/DashboardLayout';
import { Badge, Button, EmptyState, FormField, Input, KpiChip, Modal, ScorecardBookingCard, Select, SkeletonTable } from '../components/ui';
import { toAmount, resolveBookingTotalAmount } from '../hooks/booking/bookingUtils';
import { composeSlotLabel } from '../utils/slotName';

// Parse a booking's stored date (string 'YYYY-MM-DD', ISO, or Date) safely.
const parseBookingDate = (value) => {
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    try {
      return value.includes('T') ? parseISO(value) : parseISO(`${value}T00:00:00`);
    } catch {
      return new Date(value);
    }
  }
  return new Date(value);
};

const safeFormat = (value, fmt, fallback = '—') => {
  try {
    const d = parseBookingDate(value);
    if (Number.isNaN(d.getTime())) return fallback;
    return format(d, fmt);
  } catch {
    return fallback;
  }
};

// Midnight timestamp for a date value, or null when unparseable.
const dateOnlyTime = (value) => {
  const d = parseBookingDate(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

const todayStartTime = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
};

// GA-007: a booking dated today belongs to "Today" regardless of whether its
// slot end time has already passed, so a just-created same-day booking is
// never hidden away under "Past".
const tabForBooking = (booking) => {
  if (!booking) return null;
  if (booking.status === 'cancelled' || booking.status === 'archived') return 'past';
  const t = dateOnlyTime(booking.date);
  if (t == null) return 'upcoming';
  const today = todayStartTime();
  if (t === today) return 'today';
  return t > today ? 'upcoming' : 'past';
};

const MyBookings = () => {
  const { bookings, loading } = useBookings();
  const { cancelBooking, archiveBooking } = useBookingActions();
  const { markPaymentComplete } = usePayments();
  const { slotConfiguration } = useSlotConfiguration();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('upcoming');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const [archivingId, setArchivingId] = useState(null);
  const [markingPaymentId, setMarkingPaymentId] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedBookingForPayment, setSelectedBookingForPayment] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);
  const initialTabResolved = useRef(false);

  // Categorize bookings: Upcoming / Today / Past by date only (GA-007).
  const categorizedBookings = useMemo(() => {
    const grouped = { upcoming: [], today: [], past: [] };
    bookings.forEach((booking) => {
      grouped[tabForBooking(booking) || 'upcoming'].push(booking);
    });
    return grouped;
  }, [bookings]);

  // GA-007: when the page is entered right after creating a booking, land on
  // the tab that contains that booking so it is immediately visible.
  // 1) Prefer an explicit booking id/date passed via navigation state.
  // 2) Otherwise treat a booking recorded in the last few minutes as
  //    "just created" and land on its tab.
  // 3) Otherwise keep the default "Upcoming" tab.
  useEffect(() => {
    if (initialTabResolved.current || loading || bookings.length === 0) return;
    initialTabResolved.current = true;

    const state = location.state || {};
    const stateId = state.newBookingId ?? state.bookingId ?? state.createdBookingId ?? null;
    const stateDate = state.newBookingDate ?? state.bookingDate ?? null;

    let target = null;
    if (stateId != null) {
      target = tabForBooking(bookings.find((b) => String(b.id) === String(stateId)));
    }
    if (!target && stateDate) {
      target = tabForBooking({ date: stateDate, status: 'confirmed' });
    }
    if (!target) {
      const cutoff = Date.now() - 10 * 60 * 1000;
      const justCreated = bookings
        .filter((b) => {
          if (!b.bookingDate) return false;
          const created = parseBookingDate(b.bookingDate).getTime();
          return !Number.isNaN(created) && created >= cutoff;
        })
        .sort((a, b) => parseBookingDate(b.bookingDate) - parseBookingDate(a.bookingDate))[0];
      if (justCreated) target = tabForBooking(justCreated);
    }
    if (target) setActiveTab(target);
  }, [bookings, loading, location.state]);

  // Get bookings for active tab
  const getActiveBookings = () => {
    let filtered = categorizedBookings[activeTab] || [];

    if (serviceFilter !== 'all') {
      filtered = filtered.filter(booking => {
        const serviceType = booking.serviceType || 'cricket_match';
        return serviceType === serviceFilter;
      });
    }

    const q = search.trim().toLowerCase();
    if (q) {
      filtered = filtered.filter((booking) => {
        const haystack = [
          booking.serviceName,
          booking.groundName,
          booking.contactName,
          booking.contactPhone,
          booking.teamDetails?.teamA?.name,
          booking.teamDetails?.teamB?.name,
          booking.timeSlot,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      });
    }

    return filtered.sort((a, b) => {
      const dateA = new Date(a.date + ' ' + (a.timeSlot || '').split('-')[0]);
      const dateB = new Date(b.date + ' ' + (b.timeSlot || '').split('-')[0]);
      return activeTab === 'past' ? dateB - dateA : dateA - dateB;
    });
  };

  const handleCancelBooking = (bookingId) => {
    const booking = bookings.find(b => b.id === bookingId);
    setBookingToCancel(booking);
    setShowCancelConfirm(true);
  };

  const confirmCancelBooking = () => {
    if (bookingToCancel) {
      setCancellingId(bookingToCancel.id);
      setTimeout(() => {
        cancelBooking(bookingToCancel.id);
        setCancellingId(null);
        setShowCancelConfirm(false);
        setBookingToCancel(null);
      }, 1000);
    }
  };

  const handleArchiveBooking = (bookingId) => {
    setArchivingId(bookingId);
    setTimeout(() => {
      archiveBooking(bookingId);
      setArchivingId(null);
    }, 1000);
  };

  const handleOpenPaymentModal = (booking) => {
    setSelectedBookingForPayment(booking);
    setShowPaymentModal(true);
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedBookingForPayment(null);
  };

  const handleConfirmPayment = async (paymentInfo) => {
    if (!selectedBookingForPayment) return;
    const bookingId = selectedBookingForPayment.id;
    setShowPaymentModal(false);
    setSelectedBookingForPayment(null);
    setMarkingPaymentId(bookingId);
    try {
      await markPaymentComplete(bookingId, paymentInfo);
    } finally {
      setMarkingPaymentId(null);
    }
  };

  const getResolvedTotalAmount = (booking) => resolveBookingTotalAmount(booking, slotConfiguration);

  const getPaymentStatus = (booking) => {
    const totalAmount = getResolvedTotalAmount(booking);
    const advanceAmount = toAmount(booking.advanceAmount) ?? toAmount(booking.paymentDetails?.advanceAmount) ?? 0;
    const storedBalance = toAmount(booking.balanceAmount) ?? toAmount(booking.paymentDetails?.balanceAmount);
    const computedBalance = Math.max(0, totalAmount - advanceAmount);
    const balanceAmount = (storedBalance !== null && Math.abs(storedBalance - computedBalance) < 0.01)
      ? storedBalance
      : computedBalance;
    
    if (balanceAmount > 0) {
      return { 
        status: 'pending', 
        label: `Pending: ₹${balanceAmount.toFixed(2)}`, 
        variant: 'warning',
        balanceAmount 
      };
    }
    return { status: 'fully_paid', label: 'Fully Paid', variant: 'success' };
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'cancelled':
        return 'danger';
      case 'completed':
        return 'info';
      case 'archived':
        return 'neutral';
      default:
        return 'neutral';
    }
  };

  const getServiceTypeLabel = (type) => {
    const labels = {
      cricket_match: 'Cricket Match',
      practice_net: 'Practice Net',
      open_net: 'Open Net'
    };
    return labels[type] || type;
  };

  // Short label for who occupies the booking, for the card headline.
  const getParticipantLabel = (booking) => {
    if (booking.teamDetails) {
      const a = booking.teamDetails.teamA?.name || 'Team A';
      const b = booking.teamDetails.teamB?.name || 'Team B';
      return `${a} vs ${b}`;
    }
    if (booking.contactName) return booking.contactName;
    if (booking.guestList?.length) {
      return `${booking.guestList.length} guest${booking.guestList.length !== 1 ? 's' : ''}`;
    }
    return `${booking.playerCount || 0} participant${(booking.playerCount || 0) !== 1 ? 's' : ''}`;
  };

  // Single, non-duplicated slot label ("Name (HH:MM-HH:MM)" or clean range).
  const getSlotLabel = (booking) => {
    if (!booking.timeSlot && !booking.slotName) return '';
    return composeSlotLabel(booking.slotName, booking.timeSlot);
  };

  const activeBookings = getActiveBookings();
  const allBookings = bookings;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-5 md:space-y-8">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-ga-text-primary mb-2">My Bookings</h1>
          </div>
          <SkeletonTable rows={6} cols={4} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-5 md:space-y-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-ga-text-primary mb-2">My Bookings</h1>
        <p className="text-ga-text-muted">
          Manage every scheduled slot across matches, practice nets, and open nets
        </p>
      </div>

      {/* Stat chips */}
      {(() => {
        const pendingBookings = allBookings.filter(
          (b) => b.status !== 'cancelled' && getPaymentStatus(b).status === 'pending'
        );
        const pendingAmount = pendingBookings.reduce(
          (sum, b) => sum + (getPaymentStatus(b).balanceAmount || 0), 0
        );
        return (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
            <KpiChip label="Total" value={allBookings.length} icon={Calendar} accent="primary" />
            <KpiChip
              label="Upcoming"
              value={categorizedBookings.upcoming.length}
              icon={Clock}
              accent="info"
              onClick={() => setActiveTab('upcoming')}
            />
            <KpiChip
              label="Today"
              value={categorizedBookings.today.length}
              icon={CalendarCheck}
              accent="success"
              onClick={() => setActiveTab('today')}
            />
            <KpiChip
              label="Past"
              value={categorizedBookings.past.length}
              icon={Archive}
              accent="primary"
              onClick={() => setActiveTab('past')}
            />
            <KpiChip
              label={`Pending (${pendingBookings.length})`}
              value={`₹${Number(pendingAmount).toLocaleString('en-IN')}`}
              icon={DollarSign}
              accent="warning"
            />
          </div>
        );
      })()}

      {/* Tabs */}
      <div className="border-b border-ga-border-subtle">
        <nav className="-mb-px flex space-x-4 md:space-x-8 overflow-x-auto whitespace-nowrap pb-1">
          {[
            { id: 'upcoming', label: 'Upcoming', count: categorizedBookings.upcoming.length },
            { id: 'today', label: 'Today', count: categorizedBookings.today.length },
            { id: 'past', label: 'Past', count: categorizedBookings.past.length }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-ga-primary text-ga-primary'
                  : 'border-transparent text-ga-text-muted hover:text-ga-text-primary hover:border-ga-border-subtle'
              }`}
            >
              {tab.label}
              <span className={`ml-2 py-0.5 px-2 rounded-ga-full text-xs ${
                activeTab === tab.id
                  ? 'bg-ga-primary-soft text-ga-primary'
                  : 'bg-ga-bg-surface-subtle text-ga-text-muted'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Search + Filter */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ga-text-subtle" />
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, phone, team…"
            className="pl-9"
          />
        </div>
        <Filter className="h-5 w-5 text-ga-text-subtle" />
        <FormField className="w-full sm:w-auto">
          <Select
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
            className="w-full sm:w-auto"
          >
            <option value="all">All Services</option>
            <option value="cricket_match">Cricket Matches</option>
            <option value="practice_net">Practice Nets</option>
            <option value="open_net">Open Nets</option>
          </Select>
        </FormField>
        <span className="text-sm text-ga-text-muted">
          {activeBookings.length} booking{activeBookings.length !== 1 ? 's' : ''} found
        </span>
      </div>

      {/* Bookings List */}
      {activeBookings.length === 0 ? (
        <EmptyState
          card
          icon={Calendar}
          title={
            activeTab === 'upcoming' ? 'No Upcoming Bookings'
              : activeTab === 'today' ? 'No Bookings Today'
                : 'No Past Bookings'
          }
          description={
            activeTab === 'upcoming'
              ? 'You have no upcoming bookings. Book a slot to get started.'
              : activeTab === 'today'
                ? 'No bookings are scheduled for today.'
                : 'Your completed and cancelled bookings will appear here.'
          }
          action={
            activeTab !== 'past' ? (
              <Button as={Link} to="/dashboard/book-now">
                Book a slot →
              </Button>
            ) : null
          }
        />
      ) : (
        <div className="space-y-3">
          {activeBookings.map((booking) => {
            const isOpen = expandedId === booking.id;
            const paymentStatus = getPaymentStatus(booking);
            const totalAmount = getResolvedTotalAmount(booking);
            const paidAmount = toAmount(booking.advanceAmount) ?? toAmount(booking.paymentDetails?.advanceAmount) ?? 0;
            const storedBalance = toAmount(booking.balanceAmount) ?? toAmount(booking.paymentDetails?.balanceAmount);
            const computedBalance = Math.max(0, totalAmount - paidAmount);
            const balanceAmount = (storedBalance !== null && Math.abs(storedBalance - computedBalance) < 0.01)
              ? Math.max(0, storedBalance)
              : computedBalance;
            const slotLabel = getSlotLabel(booking);
            const canCollect = paymentStatus.status === 'pending' && booking.status !== 'cancelled';

            return (
              <ScorecardBookingCard
                key={booking.id}
                title={getParticipantLabel(booking)}
                service={booking.serviceName || booking.groundName}
                ground={booking.serviceType ? getServiceTypeLabel(booking.serviceType) : null}
                time={`${safeFormat(booking.date, 'EEE, dd MMM yyyy')}${slotLabel ? ` · ${slotLabel}` : ''}`}
                status={booking.status === 'archived' ? 'completed' : booking.status}
                amount={`₹${Number(totalAmount).toLocaleString('en-IN')}`}
                paymentStatus={paymentStatus.status === 'pending' ? 'pending' : 'paid'}
                actions={(
                  <>
                    {canCollect && (
                      <Button
                        onClick={() => handleOpenPaymentModal(booking)}
                        disabled={markingPaymentId === booking.id}
                        className="!px-3 !py-1.5 !text-xs"
                      >
                        <DollarSign className="h-3.5 w-3.5" /> Collect
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      onClick={() => setExpandedId(isOpen ? null : booking.id)}
                      aria-expanded={isOpen}
                      className="!px-3 !py-1.5 !text-xs"
                    >
                      Details
                      <ChevronDown
                        className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                        aria-hidden="true"
                      />
                    </Button>
                  </>
                )}
              >
                {isOpen && (
                  <div className="mt-3 border-t border-ga-border-subtle pt-4">
                    {/* Status + payment badges */}
                    <div className="mb-4 flex flex-wrap items-center gap-2">
                      {booking.serviceType && (
                        <Badge variant="info">{getServiceTypeLabel(booking.serviceType)}</Badge>
                      )}
                      <Badge variant={getStatusVariant(booking.status)}>
                        {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                      </Badge>
                      <Badge variant={paymentStatus.variant}>{paymentStatus.label}</Badge>
                    </div>

                    {/* Quick facts */}
                    <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                      <div className="flex items-center text-sm text-ga-text-muted">
                        <Calendar className="mr-2 h-4 w-4" />
                        <span>{safeFormat(booking.date, 'MMM dd, yyyy')}</span>
                      </div>
                      <div className="flex items-center text-sm text-ga-text-muted">
                        <Clock className="mr-2 h-4 w-4" />
                        <span>{slotLabel || '—'}</span>
                      </div>
                      <div className="flex items-center text-sm text-ga-text-muted">
                        <MapPin className="mr-2 h-4 w-4" />
                        <span>{booking.venue || booking.location || `Service #${booking.serviceId || booking.groundId}`}</span>
                      </div>
                      <div className="text-sm">
                        <div className="font-semibold text-ga-primary">₹{totalAmount.toFixed(2)}</div>
                        <div className={`text-xs ${paymentStatus.status === 'pending' ? 'text-ga-warning' : 'text-ga-success'}`}>
                          Paid ₹{paidAmount.toFixed(2)} · Balance ₹{balanceAmount.toFixed(2)}
                        </div>
                      </div>
                    </div>

                    {/* Team Details */}
                    {booking.teamDetails && (
                      <div className="mb-4 border-t border-ga-border-subtle pt-4">
                        <h4 className="mb-2 flex items-center font-medium text-ga-text-primary">
                          <Users className="mr-2 h-4 w-4" />
                          Team Details
                        </h4>
                        <div className="grid gap-4 md:grid-cols-2">
                          {booking.teamDetails.teamA && (
                            <div className="rounded-ga-sm bg-ga-info/15 p-3">
                              <p className="mb-1 font-medium text-ga-text-primary">{booking.teamDetails.teamA.name}</p>
                              <p className="text-sm text-ga-text-muted">Captain: {booking.teamDetails.teamA.captain}</p>
                              {booking.teamDetails.teamA.players && (
                                <p className="mt-1 text-xs text-ga-text-subtle">{booking.teamDetails.teamA.players.length} players</p>
                              )}
                            </div>
                          )}
                          {booking.teamDetails.teamB && (
                            <div className="rounded-ga-sm bg-ga-success/15 p-3">
                              <p className="mb-1 font-medium text-ga-text-primary">{booking.teamDetails.teamB.name}</p>
                              <p className="text-sm text-ga-text-muted">Captain: {booking.teamDetails.teamB.captain}</p>
                              {booking.teamDetails.teamB.players && (
                                <p className="mt-1 text-xs text-ga-text-subtle">{booking.teamDetails.teamB.players.length} players</p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Guest List */}
                    {booking.guestList && booking.guestList.length > 0 && (
                      <div className="mb-4 border-t border-ga-border-subtle pt-4">
                        <h4 className="mb-2 font-medium text-ga-text-primary">Guest List</h4>
                        <div className="flex flex-wrap gap-2">
                          {booking.guestList.map((guest, index) => (
                            <Badge key={index} variant="neutral" className="px-3 py-1 text-sm">
                              {guest.name || `Guest ${index + 1}`}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Contact */}
                    <div className="border-t border-ga-border-subtle pt-4">
                      <h4 className="mb-2 font-medium text-ga-text-primary">Contact Information</h4>
                      <div className="grid gap-4 text-sm md:grid-cols-3">
                        <div className="flex items-center text-ga-text-muted">
                          <User className="mr-2 h-4 w-4" /><span>{booking.contactName || '—'}</span>
                        </div>
                        <div className="flex items-center text-ga-text-muted">
                          <Phone className="mr-2 h-4 w-4" /><span>{booking.contactPhone || '—'}</span>
                        </div>
                        <div className="flex items-center text-ga-text-muted">
                          <Mail className="mr-2 h-4 w-4" /><span>{booking.contactEmail || '—'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Special Requests */}
                    {booking.specialRequests && (
                      <div className="mt-4 border-t border-ga-border-subtle pt-4">
                        <h4 className="mb-2 font-medium text-ga-text-primary">Special Requests</h4>
                        <p className="text-sm text-ga-text-muted">{booking.specialRequests}</p>
                      </div>
                    )}

                    {/* Booked on */}
                    <div className="mt-4 border-t border-ga-border-subtle pt-4">
                      <p className="text-xs text-ga-text-subtle">
                        Booked on: {booking.bookingDate ? safeFormat(booking.bookingDate, "MMM dd, yyyy 'at' h:mm a", 'N/A') : 'N/A'}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="mt-4 flex flex-wrap gap-2 border-t border-ga-border-subtle pt-4">
                      {booking.status === 'confirmed' && (
                        <Button variant="ghost" onClick={() => setEditingBooking(booking)} className="text-ga-info">
                          <Edit className="h-4 w-4" /> Edit
                        </Button>
                      )}
                      {booking.status === 'confirmed' && activeTab !== 'past' && (
                        <Button variant="ghost" onClick={() => handleCancelBooking(booking.id)} disabled={cancellingId === booking.id} className="text-ga-danger">
                          <Trash2 className="h-4 w-4" /> {cancellingId === booking.id ? 'Cancelling…' : 'Cancel'}
                        </Button>
                      )}
                      {booking.status === 'completed' && activeTab === 'past' && (
                        <Button variant="secondary" onClick={() => handleArchiveBooking(booking.id)} disabled={archivingId === booking.id}>
                          <Archive className="h-4 w-4" /> {archivingId === booking.id ? 'Archiving…' : 'Archive'}
                        </Button>
                      )}
                      <Button
                        as={Link}
                        variant="secondary"
                        to={booking.groundId && booking.serviceId
                          ? `/dashboard/booking/${booking.groundId}/${booking.serviceId}`
                          : '/dashboard/grounds'}
                      >
                        Book Again
                      </Button>
                    </div>
                  </div>
                )}
              </ScorecardBookingCard>
            );
          })}
        </div>
      )}


      {/* Payment Modal */}
      {showPaymentModal && selectedBookingForPayment && (
        <PaymentModal
          booking={selectedBookingForPayment}
          onClose={handleClosePaymentModal}
          onConfirm={handleConfirmPayment}
        />
      )}

      {/* Edit Booking Modal */}
      {editingBooking && (
        <EditBookingModal
          booking={editingBooking}
          onClose={() => setEditingBooking(null)}
          onSaved={() => window.location.reload()}
        />
      )}

      <Modal
        open={showCancelConfirm && Boolean(bookingToCancel)}
        onClose={() => {
          setShowCancelConfirm(false);
          setBookingToCancel(null);
        }}
        title="Confirm Cancellation"
        size="sm"
        footer={(
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => {
                setShowCancelConfirm(false);
                setBookingToCancel(null);
              }}
            >
              Keep Booking
            </Button>
            <Button
              variant="danger"
              className="flex-1"
              onClick={confirmCancelBooking}
              disabled={cancellingId === bookingToCancel?.id}
            >
              {cancellingId === bookingToCancel?.id ? 'Cancelling...' : 'Yes, Cancel Booking'}
            </Button>
          </div>
        )}
      >
        {bookingToCancel && (
          <>
            <p className="text-ga-text-primary mb-4">
              Are you sure you want to cancel this booking?
            </p>
            <div className="bg-ga-bg-surface-subtle rounded-ga-sm p-4 space-y-2">
              <p className="font-semibold text-ga-text-primary">
                {bookingToCancel.serviceName || bookingToCancel.groundName}
              </p>
              <p className="text-sm text-ga-text-muted">
                {(() => {
                  try {
                    const date = bookingToCancel.date instanceof Date 
                      ? bookingToCancel.date 
                      : (typeof bookingToCancel.date === 'string' 
                        ? (bookingToCancel.date.includes('T') ? parseISO(bookingToCancel.date) : new Date(bookingToCancel.date))
                        : new Date(bookingToCancel.date));
                    return `${format(date, 'MMM dd, yyyy')} at ${bookingToCancel.timeSlot || 'N/A'}`;
                  } catch {
                    return `${bookingToCancel.date || 'N/A'} at ${bookingToCancel.timeSlot || 'N/A'}`;
                  }
                })()}
              </p>
              {bookingToCancel.totalAmount && (
                <p className="text-sm text-ga-text-muted">
                  Amount: ₹{bookingToCancel.totalAmount}
                </p>
              )}
            </div>
            <div className="mt-4 p-3 bg-ga-warning/15 border border-ga-warning/30 rounded-ga-sm">
              <p className="text-sm text-ga-warning">
                <AlertCircle className="h-4 w-4 inline mr-1" />
                Cancellation policies may apply. Please review before confirming.
              </p>
            </div>
          </>
        )}
      </Modal>
      </div>
    </DashboardLayout>
  );
};

export default MyBookings;
